import * as React from 'react';
import { SocialShareSheet } from './SocialShareSheet';
import { useShareSheetStore } from '@stores/shareSheetStore';
import { ShareEntityType } from '@utils/shareConfig';
import { invitePromptCaps } from '@utils/invitePromptCaps';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';

/**
 * GlobalShareSheet - A globally mounted share sheet component
 *
 * This component subscribes to the shareSheetStore and renders the SocialShareSheet
 * when triggered. It should be mounted once at the app root level (e.g., in index.tsx)
 * to enable sharing from anywhere in the app, even after navigation.
 *
 * Usage:
 * 1. Mount <GlobalShareSheet /> in index.tsx alongside <Toaster />
 * 2. Use the useShareSheet() hook or store actions to trigger the sheet:
 *    - openShareSheet(context) - queues behind any current overlay holder, opens on its turn
 *    - openShareSheet(context, { delay: 300 }) - opens after delay (e.g., after navigation)
 *    - closeShareSheet() - closes the sheet
 *
 * Post-help invite caps (C3/C4): this close handler is the SINGLE place where a
 * post-help App-invite prompt's outcome is recorded. A close after a completed
 * share (store `_didShare`) latches the caps via `recordShared()`; a close
 * without a share is a genuine dismissal - it latches via `recordDismissed()`
 * and emits `share_prompt_dismissed`. Both paths key off `context.sharerId`,
 * which for post-help prompts is the signed-in user's real id.
 */
export const GlobalShareSheet: React.FC = () => {
  const isOpen = useShareSheetStore(state => state.isOpen);
  const context = useShareSheetStore(state => state.context);
  const closeShareSheet = useShareSheetStore(state => state.closeShareSheet);
  const setBackHandler = useShareSheetStore(state => state.setBackHandler);
  const { trackEvent } = useAmplitudeTracking();
  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (open) return;

      // Record the post-help prompt outcome for App-invite contexts before the
      // context is cleared. Only the post-help prompt surfaces drive caps; other
      // App-invite surfaces (banner/generic) leave the caps untouched.
      //
      // Gate on inviteVariant, NOT scenario: buildAppInviteContext stamps a
      // PostHelp* scenario on EVERY App context (banner/generic fall through to
      // PostHelpHelper), so a scenario check would mis-classify the C4 banner as a
      // post-help prompt and latch its caps off on a plain close. inviteVariant is
      // the true discriminator - only 'helper'/'asker' are post-help prompts.
      const current = useShareSheetStore.getState().context;
      const isPostHelpPrompt =
        current?.entityType === ShareEntityType.App &&
        (current.inviteVariant === 'helper' || current.inviteVariant === 'asker');

      if (isPostHelpPrompt && current.sharerId) {
        const caps = invitePromptCaps(current.sharerId);
        if (useShareSheetStore.getState()._didShare) {
          caps.recordShared();
        } else {
          caps.recordDismissed();
          trackEvent(ANALYTICS_EVENTS.share_prompt_dismissed, {
            side: current.inviteVariant,
            trigger: 'post_help_chat',
          });
        }
      }

      closeShareSheet('user');
    },
    [closeShareSheet, trackEvent],
  );

  // Android back on the held grant runs the same close path a tap does (§6.5).
  React.useLayoutEffect(() => {
    setBackHandler(() => {
      handleOpenChange(false);
    });
    return () => {
      setBackHandler(null);
    };
  }, [setBackHandler, handleOpenChange]);
  // Don't render if no context (prevents flash of empty sheet)
  if (!context) {
    return null;
  }

  return <SocialShareSheet open={isOpen} onOpenChange={handleOpenChange} context={context} />;
};
